import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
    X,
    User,
    Users,
    Car,
    Shield,
    Wallet,
    Percent,
    StickyNote,
    Scale,
    ExternalLink,
    Loader2,
    Pencil,
    Banknote,
    FileText,
    CheckCircle2,
    AlertTriangle,
    Clock,
    Phone,
    Mail,
    MapPin,
    Building2,
    ChevronRight,
    Hash,
} from "lucide-react";
import { sigortaDosyaKartOzet } from "../lib/api";

function fmtDate(v) {
    if (!v) return "—";
    try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return String(v);
        return d.toLocaleDateString("tr-TR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return String(v);
    }
}

function fmtMoney(v) {
    if (v == null || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        maximumFractionDigits: 0,
    }).format(n);
}

function initials(name) {
    return String(name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

function computePanelLayout(anchor) {
    const margin = 14;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    if (vw < 768) {
        return {
            left: margin,
            top: margin,
            width: vw - margin * 2,
            height: vh - margin * 2,
        };
    }

    const panelW = Math.min(960, Math.max(720, Math.floor(vw * 0.56)));
    const panelH = Math.min(vh - margin * 2, Math.max(640, Math.floor(vh * 0.88)));

    let left = vw - panelW - margin;
    let top = anchor
        ? Math.max(margin, Math.min(anchor.top - 12, vh - panelH - margin))
        : Math.max(margin, (vh - panelH) / 2);

    if (anchor && anchor.top + 80 > vh - panelH) {
        top = Math.max(margin, vh - panelH - margin);
    }

    return { left, top, width: panelW, height: panelH };
}

function Badge({ children, tone = "gold" }) {
    const tones = {
        gold: "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB]",
        violet: "border-violet-500/35 bg-violet-500/12 text-violet-200",
        emerald: "border-emerald-500/35 bg-emerald-500/12 text-emerald-200",
        rose: "border-rose-500/35 bg-rose-500/12 text-rose-200",
        sky: "border-sky-500/35 bg-sky-500/12 text-sky-200",
        amber: "border-amber-500/35 bg-amber-500/12 text-amber-200",
        slate: "border-white/15 bg-white/5 text-slate-300",
    };
    return (
        <span
            className={`inline-flex items-center rounded-md border px-2.5 py-1 text-[11px] font-medium ${tones[tone] || tones.slate}`}
        >
            {children}
        </span>
    );
}

function KpiTile({ label, value, sub, tone = "default" }) {
    const tones = {
        default: "border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent",
        gold: "border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/10 to-transparent",
        danger: "border-rose-500/30 bg-gradient-to-br from-rose-500/10 to-transparent",
        success: "border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-transparent",
    };
    return (
        <div className={`rounded-xl border p-4 ${tones[tone] || tones.default}`}>
            <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-1.5">
                {label}
            </div>
            <div className="text-xl font-semibold font-mono-jb text-slate-100 leading-tight">
                {value}
            </div>
            {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

function SectionCard({ title, icon: Icon, children, className = "" }) {
    return (
        <section
            className={`rounded-2xl border border-white/10 bg-[#0B0F19]/70 overflow-hidden shadow-lg shadow-black/20 ${className}`}
        >
            <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-white/8 bg-gradient-to-r from-black/30 to-transparent">
                {Icon && <Icon className="h-4 w-4 text-[#D4AF37]" />}
                <h3 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-300">
                    {title}
                </h3>
            </div>
            <div className="p-5">{children}</div>
        </section>
    );
}

function InfoGrid({ items }) {
    const visible = items.filter((i) => i.value != null && i.value !== "" && i.value !== "—");
    if (!visible.length) return null;
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
            {visible.map((item) => (
                <div key={item.label} className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                        {item.label}
                    </div>
                    <div
                        className={`text-sm text-slate-100 break-words ${item.mono ? "font-mono-jb" : ""}`}
                    >
                        {item.value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function TabButton({ active, icon: Icon, label, count, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border ${
                active
                    ? "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB] shadow-inner shadow-[#D4AF37]/5"
                    : "border-transparent text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
            }`}
        >
            {Icon && <Icon className={`h-4 w-4 shrink-0 ${active ? "text-[#D4AF37]" : ""}`} />}
            <span className="text-sm font-medium flex-1">{label}</span>
            {count != null && count > 0 && (
                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-md bg-black/30 text-slate-400">
                    {count}
                </span>
            )}
            {active && <ChevronRight className="h-3.5 w-3.5 opacity-60" />}
        </button>
    );
}

export default function SigortaDosyaDetayDrawer({ open, row, anchor, onClose, onEdit }) {
    const [ozet, setOzet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("ozet");

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    useEffect(() => {
        if (!open || !row?.id) {
            setOzet(null);
            setTab("ozet");
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        sigortaDosyaKartOzet(row.id)
            .then((data) => {
                if (!cancelled) setOzet(data);
            })
            .catch(() => {
                if (!cancelled) setOzet(null);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, row?.id]);

    const layout = useMemo(() => computePanelLayout(anchor), [anchor]);

    if (!open || !row) return null;

    const d = ozet || {};
    const mu = d.muvekkil || {};
    const muAd = mu.ad_soyad || row.muvekkil_ad_soyad || row.muvekkil_label || "—";

    const sureTone =
        String(d.sure_durumu || "").toUpperCase() === "GECTI"
            ? "danger"
            : ["YAKLASIYOR", "SON_GUN"].includes(String(d.sure_durumu || "").toUpperCase())
              ? "amber"
              : "emerald";

    const tabs = [
        { id: "ozet", label: "Dosya Özeti", icon: Shield },
        { id: "muvekkil", label: "Müvekkil & Aracı", icon: Users, count: (d.aracilar || []).length },
        { id: "odeme", label: "Ödemeler", icon: Wallet, count: d.odeme_ozet?.kayit_sayisi },
        { id: "dk", label: "Değer Kaybı", icon: Percent },
        { id: "notlar", label: "Notlar", icon: StickyNote, count: d.not_sayisi },
    ];

    return createPortal(
        <>
            <div
                className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-[3px]"
                onClick={onClose}
                aria-hidden
            />
            <aside
                className="fixed z-[301] flex flex-col rounded-2xl border border-[#D4AF37]/25 bg-[#080C14]/98 shadow-2xl shadow-black/70 overflow-hidden animate-in fade-in slide-in-from-right-4 duration-200"
                style={{
                    left: layout.left,
                    top: layout.top,
                    width: layout.width,
                    height: layout.height,
                }}
                role="dialog"
                aria-modal="true"
                aria-label="Sigorta dosyası detayı"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Hero */}
                <header className="shrink-0 relative overflow-hidden border-b border-white/10">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/18 via-violet-900/20 to-sky-900/10" />
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImEiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0ibm9uZSIvPjxwYXRoIGQ9Ik0zMCAwaDEwdjEwSDMweiIgZmlsbD0iI2ZmZiIgZmlsbC1vcGFjaXR5PSIuMDMiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IGZpbGw9InVybCgjYSkiIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiLz48L3N2Zz4=')] opacity-40" />
                    <div className="relative px-6 py-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                    <Badge tone="gold">
                                        <Hash className="h-3 w-3 mr-1 inline" />
                                        {d.dosya_no || row.dosya_no || `#${row.id}`}
                                    </Badge>
                                    {d.dosya_durumu_display && (
                                        <Badge tone="violet">{d.dosya_durumu_display}</Badge>
                                    )}
                                    {d.dosya_durumu_display && (
                                        <Badge tone="slate">{d.dosya_durumu_display}</Badge>
                                    )}
                                    {d.yatar_acilacak_mi && (
                                        <Badge tone="amber">Yatar</Badge>
                                    )}
                                    {d.yatar_wa_durum_label ? (
                                        <Badge tone={d.yatar_wa_durum === 'ONAYLANDI' ? 'gold' : d.yatar_wa_durum === 'REDDEDILDI' ? 'rose' : 'sky'}>
                                            WA: {d.yatar_wa_durum_label}
                                        </Badge>
                                    ) : null}
                                </div>
                                <h2 className="text-2xl font-display font-semibold text-white truncate">
                                    {muAd}
                                </h2>
                                <p className="text-sm text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <span className="inline-flex items-center gap-1.5 font-mono-jb text-[#F3E5AB]/90">
                                        <Car className="h-3.5 w-3.5" />
                                        {d.plaka || row.plaka || "—"}
                                    </span>
                                    <span>{d.basvuru_konusu_display || d.basvuru_konusu || "—"}</span>
                                    <span>{d.sigorta_sirketi || "—"}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => onEdit?.(row)}
                                    className="h-10 px-4 rounded-xl border border-white/15 bg-black/20 text-slate-200 hover:bg-white/5 text-sm inline-flex items-center gap-2 transition"
                                >
                                    <Pencil className="h-4 w-4" />
                                    Düzenle
                                </button>
                                <Link
                                    to={`/e/sigorta-dosyalari/${row.id}`}
                                    onClick={onClose}
                                    className="h-10 px-4 rounded-xl border border-sky-500/35 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25 text-sm inline-flex items-center gap-2 transition"
                                >
                                    <ExternalLink className="h-4 w-4" />
                                    Tam sayfa
                                </Link>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="h-10 w-10 rounded-xl border border-white/15 bg-black/20 text-slate-400 hover:text-white hover:bg-rose-500/15 hover:border-rose-500/30 flex items-center justify-center transition"
                                    aria-label="Kapat"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        {!loading && (
                            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3 mt-5">
                                <KpiTile
                                    label="Kalan Gün"
                                    value={d.kalan_gun ?? "—"}
                                    sub={d.sure_durumu_display}
                                    tone={sureTone === "danger" ? "danger" : sureTone === "amber" ? "default" : "default"}
                                />
                                <KpiTile
                                    label="Rayiç Değer"
                                    value={fmtMoney(d.arac_rayic_degeri)}
                                    tone="gold"
                                />
                                <KpiTile
                                    label="Gerçek DK"
                                    value={d.deger_kaybi?.gercek != null ? fmtMoney(d.deger_kaybi.gercek) : "—"}
                                    sub={d.deger_kaybi?.baz != null ? `Baz: ${fmtMoney(d.deger_kaybi.baz)}` : null}
                                />
                                <KpiTile
                                    label="Ödeme Toplam"
                                    value={fmtMoney(d.odeme_ozet?.toplam_tutar)}
                                    sub={
                                        d.odeme_ozet?.odenen_tutar != null
                                            ? `Ödenen: ${fmtMoney(d.odeme_ozet.odenen_tutar)}`
                                            : null
                                    }
                                    tone="success"
                                />
                                <KpiTile
                                    label="Eksik Evrak"
                                    value={d.evrak?.eksik ?? "0"}
                                    sub={`Toplam ${d.evrak?.toplam ?? 0} evrak`}
                                    tone={Number(d.evrak?.eksik) > 0 ? "danger" : "success"}
                                />
                            </div>
                        )}
                    </div>
                </header>

                <div className="flex flex-1 min-h-0">
                    {/* Sidebar tabs */}
                    <nav className="hidden md:flex flex-col w-[220px] shrink-0 border-r border-white/8 bg-black/20 p-3 gap-1 overflow-y-auto">
                        {tabs.map((t) => (
                            <TabButton
                                key={t.id}
                                active={tab === t.id}
                                icon={t.icon}
                                label={t.label}
                                count={t.count}
                                onClick={() => setTab(t.id)}
                            />
                        ))}
                    </nav>

                    {/* Mobile tabs */}
                    <div className="md:hidden shrink-0 flex gap-1 overflow-x-auto px-4 py-2 border-b border-white/8 bg-black/20">
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`shrink-0 px-3 py-1.5 rounded-lg text-xs border transition ${
                                    tab === t.id
                                        ? "border-[#D4AF37]/40 bg-[#D4AF37]/12 text-[#F3E5AB]"
                                        : "border-white/10 text-slate-400"
                                }`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-5 md:p-6 space-y-5 bg-gradient-to-b from-transparent to-black/20">
                        {loading && (
                            <div className="flex flex-col items-center justify-center gap-4 py-24 text-slate-400">
                                <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                                <span className="text-sm">Dosya merkezi yükleniyor…</span>
                            </div>
                        )}

                        {!loading && tab === "ozet" && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                <SectionCard title="Dosya Bilgileri" icon={Shield}>
                                    <InfoGrid
                                        items={[
                                            { label: "Konu", value: d.basvuru_konusu_display },
                                            { label: "Tür", value: d.basvuru_turu_display },
                                            { label: "Dosya tipi", value: d.dosya_tipi_display },
                                            { label: "Sigorta şirketi", value: d.sigorta_sirketi },
                                            { label: "Poliçe no", value: d.polis_no, mono: true },
                                            { label: "Talep no", value: d.sigorta_talep_no, mono: true },
                                            { label: "Kaza tarihi", value: fmtDate(d.kaza_tarihi) },
                                            { label: "Kayıt tarihi", value: fmtDate(d.kayit_tarihi) },
                                            { label: "Zamanaşımı", value: fmtDate(d.zamanasimi_son_tarihi) },
                                            { label: "Avukat", value: d.avukat_label },
                                            { label: "Yatar", value: d.yatar_acilacak_mi ? "Evet" : "Hayır" },
                                        ]}
                                    />
                                </SectionCard>
                                <SectionCard title="Araç Bilgileri" icon={Car}>
                                    <InfoGrid
                                        items={[
                                            { label: "Plaka", value: d.plaka, mono: true },
                                            {
                                                label: "Marka / Model",
                                                value: [d.marka, d.model, d.model_yili].filter(Boolean).join(" "),
                                            },
                                            { label: "KM", value: d.km != null ? `${Number(d.km).toLocaleString("tr-TR")} km` : null },
                                            { label: "Araç tipi", value: d.arac_tipi_display },
                                            { label: "Rayiç", value: fmtMoney(d.arac_rayic_degeri) },
                                            { label: "Kusur", value: d.kusur_orani != null ? `%${d.kusur_orani}` : null },
                                        ]}
                                    />
                                </SectionCard>
                                {d.tahkim && (
                                    <SectionCard title="Tahkim" icon={Scale}>
                                        <InfoGrid
                                            items={[
                                                { label: "Aşama", value: d.tahkim.asama_display },
                                                { label: "Başvuru no", value: d.tahkim.basvuru_no, mono: true },
                                                { label: "Hakem", value: d.tahkim.hakem },
                                                { label: "Ödeme durumu", value: d.tahkim.odeme_durumu_display },
                                            ]}
                                        />
                                    </SectionCard>
                                )}
                                {d.icra && (
                                    <SectionCard title="İcra" icon={Building2}>
                                        <InfoGrid
                                            items={[
                                                { label: "Dosya no", value: d.icra.icra_dosya_no, mono: true },
                                                { label: "Durum", value: d.icra.durum_display || d.icra.durum },
                                                { label: "Borç", value: fmtMoney(d.icra.borc_tutari) },
                                            ]}
                                        />
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {!loading && tab === "muvekkil" && (
                            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                                <SectionCard title="Müvekkil" icon={User}>
                                    <div className="flex gap-4 mb-5">
                                        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-violet-600/40 to-[#D4AF37]/30 flex items-center justify-center text-lg font-bold text-white shrink-0">
                                            {initials(muAd) || <User className="h-6 w-6" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-lg font-semibold text-white">{muAd}</div>
                                            {mu.tc && (
                                                <div className="text-xs font-mono text-slate-500 mt-0.5">
                                                    TC: {mu.tc}
                                                </div>
                                            )}
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {mu.vekalet_durumu_display && (
                                                    <Badge tone={mu.vekalet_durumu === "ALINDI" ? "emerald" : "rose"}>
                                                        Vekalet: {mu.vekalet_durumu_display}
                                                    </Badge>
                                                )}
                                                {mu.anlasma_orani != null && (
                                                    <Badge tone="slate">Anlaşma %{mu.anlasma_orani}</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-2.5 text-sm">
                                        {mu.telefon && (
                                            <div className="flex items-center gap-2 text-slate-300">
                                                <Phone className="h-4 w-4 text-slate-500" /> {mu.telefon}
                                            </div>
                                        )}
                                        {mu.email && (
                                            <div className="flex items-center gap-2 text-slate-300">
                                                <Mail className="h-4 w-4 text-slate-500" /> {mu.email}
                                            </div>
                                        )}
                                        {mu.adres && (
                                            <div className="flex items-start gap-2 text-slate-400">
                                                <MapPin className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
                                                <span className="leading-relaxed">{mu.adres}</span>
                                            </div>
                                        )}
                                        {mu.iban && (
                                            <div className="flex items-center gap-2 text-slate-300 font-mono-jb text-xs">
                                                IBAN: {mu.iban}
                                            </div>
                                        )}
                                    </div>
                                    {mu.id && (
                                        <Link
                                            to={`/e/muvekkiller/${mu.id}`}
                                            className="inline-flex items-center gap-2 mt-4 text-sm text-violet-300 hover:text-violet-200"
                                        >
                                            Müvekkil kartına git <ExternalLink className="h-4 w-4" />
                                        </Link>
                                    )}
                                </SectionCard>

                                <SectionCard
                                    title={`Aracılar (${(d.aracilar || []).length})`}
                                    icon={Users}
                                >
                                    {(d.aracilar || []).length === 0 ? (
                                        <p className="text-sm text-slate-500 italic py-4 text-center">
                                            Bu müvekkile bağlı aracı yok
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {(d.aracilar || []).map((a) => (
                                                <div
                                                    key={a.id}
                                                    className="rounded-xl border border-white/10 bg-black/25 p-4 hover:border-[#D4AF37]/20 transition"
                                                >
                                                    <div className="flex items-start justify-between gap-2">
                                                        <div>
                                                            <div className="font-semibold text-slate-100">{a.ad}</div>
                                                            <div className="text-xs text-slate-500 mt-1 space-y-0.5">
                                                                {a.telefon && <div>{a.telefon}</div>}
                                                                {a.email && <div>{a.email}</div>}
                                                            </div>
                                                        </div>
                                                        <div className="text-right">
                                                            <div className="text-[10px] text-slate-500 uppercase">Hakediş</div>
                                                            <div className="font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(a.hak_edis_miktari)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <Link
                                                        to={`/e/aracilar/${a.id}`}
                                                        className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline mt-3"
                                                    >
                                                        Aracı kartı <ExternalLink className="h-3 w-3" />
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>

                                {(d.araci_odemeleri || []).length > 0 && (
                                    <SectionCard
                                        title="Bu Dosya — Aracı Ödemeleri"
                                        icon={Banknote}
                                        className="xl:col-span-2"
                                    >
                                        <div className="overflow-x-auto rounded-xl border border-white/8">
                                            <table className="w-full text-sm">
                                                <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                    <tr>
                                                        <th className="text-left px-4 py-2.5">Aracı</th>
                                                        <th className="text-right px-4 py-2.5">Tutar</th>
                                                        <th className="text-right px-4 py-2.5">Durum</th>
                                                        <th className="text-right px-4 py-2.5">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(d.araci_odemeleri || []).map((ao) => (
                                                        <tr key={ao.id} className="border-t border-white/5">
                                                            <td className="px-4 py-3 text-slate-200">{ao.araci_ad}</td>
                                                            <td className="px-4 py-3 text-right font-mono-jb text-[#F3E5AB]">
                                                                {fmtMoney(ao.tutar)}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-slate-400">
                                                                {ao.durum_display || ao.durum}
                                                            </td>
                                                            <td className="px-4 py-3 text-right text-slate-500 text-xs">
                                                                {fmtDate(ao.tarih)}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </SectionCard>
                                )}
                            </div>
                        )}

                        {!loading && tab === "odeme" && (
                            <SectionCard title="Ödeme Takibi" icon={Wallet}>
                                {d.odeme_ozet && (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                                        <KpiTile label="Toplam" value={fmtMoney(d.odeme_ozet.toplam_tutar)} tone="gold" />
                                        <KpiTile label="Ödenen" value={fmtMoney(d.odeme_ozet.odenen_tutar)} tone="success" />
                                        <KpiTile label="Bekleyen" value={fmtMoney(d.odeme_ozet.bekleyen_tutar)} />
                                    </div>
                                )}
                                {(d.odemeler || []).length === 0 ? (
                                    <p className="text-sm text-slate-500 italic text-center py-8">
                                        Ödeme kaydı bulunmuyor
                                    </p>
                                ) : (
                                    <div className="overflow-x-auto rounded-xl border border-white/8">
                                        <table className="w-full text-sm">
                                            <thead className="bg-black/30 text-[10px] uppercase tracking-wider text-slate-500">
                                                <tr>
                                                    <th className="text-left px-4 py-2.5">Tür</th>
                                                    <th className="text-right px-4 py-2.5">Tutar</th>
                                                    <th className="text-right px-4 py-2.5">MBS Hakediş</th>
                                                    <th className="text-right px-4 py-2.5">Durum</th>
                                                    <th className="text-right px-4 py-2.5">Tarih</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(d.odemeler || []).map((o) => (
                                                    <tr key={o.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                                                        <td className="px-4 py-3.5 text-slate-200">
                                                            {o.tur_display || o.tur || "—"}
                                                        </td>
                                                        <td className="px-4 py-3.5 text-right font-mono-jb text-slate-100">
                                                            {fmtMoney(o.tutar)}
                                                        </td>
                                                        <td className="px-4 py-3.5 text-right font-mono-jb text-slate-400 text-xs">
                                                            {fmtMoney(o.mbs_hakedis)}
                                                        </td>
                                                        <td className="px-4 py-3.5 text-right">
                                                            {o.odendi ? (
                                                                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                                                                    <CheckCircle2 className="h-3.5 w-3.5" /> Ödendi
                                                                </span>
                                                            ) : (
                                                                <span className="text-amber-300 text-xs">Bekliyor</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3.5 text-right text-slate-500 text-xs">
                                                            {fmtDate(o.tarih)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <Link
                                    to={`/e/odemeler?sigorta_dosyasi=${row.id}`}
                                    onClick={onClose}
                                    className="inline-flex items-center gap-2 mt-5 text-sm text-sky-300 hover:text-sky-200"
                                >
                                    Tüm ödemeleri görüntüle <ExternalLink className="h-4 w-4" />
                                </Link>
                            </SectionCard>
                        )}

                        {!loading && tab === "dk" && (
                            <SectionCard title="Değer Kaybı Analizi" icon={Percent}>
                                {d.deger_kaybi ? (
                                    <>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
                                            <div className="rounded-2xl border border-violet-500/30 bg-gradient-to-br from-violet-500/15 to-transparent p-6 text-center">
                                                <div className="text-xs uppercase tracking-widest text-violet-300/80 mb-2">
                                                    Baz Değer Kaybı
                                                </div>
                                                <div className="text-3xl font-mono-jb font-semibold text-violet-100">
                                                    {fmtMoney(d.deger_kaybi.baz)}
                                                </div>
                                            </div>
                                            <div className="rounded-2xl border border-[#D4AF37]/35 bg-gradient-to-br from-[#D4AF37]/15 to-transparent p-6 text-center">
                                                <div className="text-xs uppercase tracking-widest text-[#F3E5AB]/80 mb-2">
                                                    Gerçek Değer Kaybı
                                                </div>
                                                <div className="text-3xl font-mono-jb font-semibold text-[#F3E5AB]">
                                                    {fmtMoney(d.deger_kaybi.gercek)}
                                                </div>
                                            </div>
                                        </div>
                                        <Link
                                            to={`/e/deger-kaybi?sigorta_dosyasi=${row.id}`}
                                            onClick={onClose}
                                            className="inline-flex items-center gap-2 text-sm text-sky-300 hover:text-sky-200"
                                        >
                                            Değer kaybı kayıtlarına git <ExternalLink className="h-4 w-4" />
                                        </Link>
                                    </>
                                ) : (
                                    <div className="text-center py-12 text-slate-500">
                                        <Percent className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                        <p>Bu dosya için değer kaybı hesabı henüz yapılmamış.</p>
                                    </div>
                                )}
                            </SectionCard>
                        )}

                        {!loading && tab === "notlar" && (
                            <SectionCard title={`Dosya Notları (${d.not_sayisi || 0})`} icon={StickyNote}>
                                {(d.notlar || []).length === 0 ? (
                                    <div className="text-center py-12 text-slate-500">
                                        <StickyNote className="h-10 w-10 mx-auto mb-3 opacity-30" />
                                        <p>Henüz not girilmemiş.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {(d.notlar || []).map((n, idx) => (
                                            <article
                                                key={`${n.kaynak}-${n.id || idx}`}
                                                className="rounded-xl border border-[#D4AF37]/20 bg-gradient-to-r from-[#D4AF37]/8 to-transparent p-4"
                                            >
                                                <div className="flex items-start justify-between gap-3 mb-2">
                                                    <div>
                                                        <div className="text-sm font-semibold text-[#F3E5AB]">
                                                            {n.konu || "Not"}
                                                        </div>
                                                        {n.yazan && (
                                                            <div className="text-xs text-slate-500 mt-0.5">
                                                                {n.yazan}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 text-xs text-slate-500 shrink-0">
                                                        <Clock className="h-3.5 w-3.5" />
                                                        {fmtDate(n.tarih)}
                                                    </div>
                                                </div>
                                                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                                                    {n.ozet}
                                                </p>
                                            </article>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>
                        )}

                        {!loading && d.kalan_gun != null && Number(d.kalan_gun) <= 20 && (
                            <div className="flex items-start gap-3 rounded-2xl border border-amber-500/35 bg-gradient-to-r from-amber-500/12 to-transparent px-5 py-4">
                                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-300 mt-0.5" />
                                <div>
                                    <div className="text-sm font-semibold text-amber-100">
                                        Zamanaşımı uyarısı
                                    </div>
                                    <div className="text-sm text-amber-200/80 mt-0.5">
                                        Dosyada zamanaşımına {d.kalan_gun} gün kaldı — acil kontrol önerilir.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </aside>
        </>,
        document.body,
    );
}
